import { useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useAiChatDocumentViewerStyles } from "./AiChatDocumentViewer.styles";
import { useAiChatDocumentViewer } from "./AiChatDocumentViewerContext";
import { DocumentEntityType, mapDocumentEntityTypeToEntityType } from "@common/components/search/SearchResultList.types";
import { PreviewableDocument } from "@common/components/documentPreview/DocumentPreview.types";
import DocumentPreviewContent from "@common/components/documentPreview/DocumentPreviewContent";
import DocumentPreviewHeader from "@common/components/documentPreview/DocumentPreviewHeader";

function AiChatDocumentViewer() {
    const { activeSource, activeCitationKey, activeAttachment, isOpen, closeViewer, closeForEntityLinkNavigation } = useAiChatDocumentViewer();
    const styles = useAiChatDocumentViewerStyles(isOpen);
    const [documentName, setDocumentName] = useState("");

    const isEntitySource = activeSource !== null && activeAttachment === null && activeSource.entityType !== DocumentEntityType.ConversationAttachment;
    const entityType = isEntitySource ? mapDocumentEntityTypeToEntityType(activeSource!.entityType) : undefined;
    const entityId = isEntitySource ? activeSource!.entityId : undefined;
    const entityTitle = isEntitySource ? activeSource!.entityTitle : undefined;

    const document = useMemo<PreviewableDocument | null>(() => {
        if (activeAttachment) {
            return {
                documentId: activeAttachment.id,
                fileName: activeAttachment.fileName,
                name: activeAttachment.fileName,
                customDocumentUrl: `/ai-chat/attachments/${activeAttachment.id}/download`,
            };
        }
        if (!activeSource) return null;
        if (activeSource.entityType === DocumentEntityType.ConversationAttachment) {
            return {
                documentId: activeSource.entityId,
                fileName: activeSource.fileName,
                name: activeSource.documentTitle,
                customDocumentUrl: `/ai-chat/attachments/${activeSource.entityId}/download`,
            };
        }
        return {
            documentId: activeSource.documentId,
            documentVersionId: activeSource.documentVersionId,
            fileName: activeSource.fileName,
            name: activeSource.documentTitle,
            entityType: entityType!,
            entityId: activeSource.entityId,
        };
    }, [activeSource, activeAttachment, entityType]);

    const previewKey = activeAttachment ? `attachment-${activeAttachment.id}` : activeCitationKey;
    const initialDocumentId = activeAttachment ? activeAttachment.id : activeSource?.documentVersionId;
    const fileName = activeAttachment?.fileName ?? activeSource?.fileName ?? documentName ?? activeSource?.documentTitle ?? "";

    return (
        <Box sx={styles.panel}>
            <DocumentPreviewHeader
                entityType={entityType}
                entityId={entityId}
                entityTitle={entityTitle}
                fileName={fileName}
                onClose={closeViewer}
                onEntityLinkNavigate={closeForEntityLinkNavigation}
            />
            <Box sx={styles.content}>
                {document && (
                    <DocumentPreviewContent
                        key={previewKey}
                        open={isOpen}
                        documents={[document]}
                        initialDocumentId={initialDocumentId}
                        initialPage={activeSource?.pageNumber}
                        // No citationNotFoundText: a chat citation points at an indexed chunk that can legitimately
                        // drift from the rendered document, and the chat bubble already shows the quoted text.
                        citationChunkContent={activeSource?.chunkContent}
                        onDocumentNameChange={setDocumentName}
                    />
                )}
            </Box>
        </Box>
    );
}

export default AiChatDocumentViewer;
